/**
 * WCAG contrast, computed in the editor (Doc 04 §5.5).
 *
 * The server validates the same thing at publish time and is what actually blocks a bad
 * config. This copy exists so the number moves while the colour picker moves — telling
 * someone their contrast failed only after they pressed publish is how they end up
 * picking colours by trial and error.
 */
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

function luminance(hex: string): number | null {
  const v = hex.trim();
  if (!HEX.test(v)) return null;
  let h = v.slice(1);
  if (h.length === 3) h = h[0]! + h[0]! + h[1]! + h[1]! + h[2]! + h[2]!;
  if (h.length === 8) h = h.slice(0, 6);
  const n = parseInt(h, 16);
  const ch = (c: number): number => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch((n >> 16) & 255) + 0.7152 * ch((n >> 8) & 255) + 0.0722 * ch(n & 255);
}

/** Returns the ratio, or null when either colour is not something we can compute. */
export function contrast(fg: string | undefined, bg: string | undefined): number | null {
  const a = luminance(fg ?? '');
  const b = luminance(bg ?? '');
  if (a === null || b === null) return null;
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

export interface ContrastVerdict {
  ratio: number | null;
  /** 4.5:1 is the AA threshold for body text (WCAG 1.4.3). */
  passes: boolean;
  text: string;
}

export function verdict(fg: string | undefined, bg: string | undefined): ContrastVerdict {
  const ratio = contrast(fg, bg);
  if (ratio === null) return { ratio: null, passes: true, text: 'nicht berechenbar' };
  const r = Math.round(ratio * 10) / 10;
  return {
    ratio: r,
    passes: ratio >= 4.5,
    text: `${r.toFixed(1)}:1 ${ratio >= 4.5 ? '✓' : '– zu wenig (min. 4,5:1)'}`,
  };
}
